import { reduce } from 'lodash/fp';
import { DateTimeUnit } from 'luxon';
import { DateTimePart, unitByDateTimePart } from '../enums';
import { ToLuxon } from './types';

type WithGet<T, P> = {
    [Property in P as `get${Capitalize<string & Property>}`]: (entity: T) => number;
};

type PartWithoutWeek = Exclude<DateTimeUnit, (typeof unitByDateTimePart)[DateTimePart.Week]>;

const accessible = <T, P extends DateTimePart>(toLuxon: ToLuxon<T>, parts: readonly P[] = []) => {
    const get =
        (partName: P) =>
        (entity: T): number => {
            const luxonEntity = toLuxon(entity);

            // Luxon uses ISO weeks, but we expect the week number to be the actual ordinal week number.
            if (partName === DateTimePart.Week) {
                const yearWeekOffset = luxonEntity.set({ month: 1, day: 1 }).get('weekday') % 7;
                const weekNumber = Math.ceil((luxonEntity.get('ordinal') + yearWeekOffset) / 7);
                return weekNumber;
            }

            const result = luxonEntity.get(unitByDateTimePart[partName] as PartWithoutWeek);

            // Luxon counts months from 1, but we expect them to be counted from 0.
            if (partName === DateTimePart.Month) {
                return result - 1;
            }

            // Luxon counts weekdays in range 1-7 (Monday = 1, Sunday = 7), but we expect them in range 0-6 (Sunday = 0, Saturday = 6).
            if (partName === DateTimePart.Day) {
                return luxonEntity.weekday % 7;
            }

            return result;
        };

    return reduce<P, WithGet<T, P>>(
        (operations, partName) => ({
            ...operations,
            [`get${partName}`]: (entity: T) => get(partName)(entity),
        }),
        {} as WithGet<T, P>,
        parts
    );
};

export default accessible;
